import { Button, Input, Item, ItemActions, ItemHeader, ItemTitle, Tooltip } from '@ibsys/kit';
import { BookmarkXIcon, CheckIcon, PencilIcon, StarIcon, XIcon } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationButton } from '@/shared/ui';
import { cn } from '@/shared/lib';
import type { TablePreset } from '@/entities/settings';

type Props = {
  preset: TablePreset;
  /** Активный (применённый) пресет — подсвечивается */
  isActive: boolean;
  onApply: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  /** Снять активный пресет; кнопка показывается только у активного пресета */
  onClearActive: () => void;
  /** Закрепить пресет отдельным пунктом левого меню или снять закрепление */
  onTogglePinned: (pinned: boolean) => void;
};

/**
 * Строка пресета в списке: применение по клику, inline-переименование, закрепление в меню,
 * удаление с подтверждением.
 */
export const PresetItem: FC<Props> = ({
  preset,
  isActive,
  onApply,
  onRename,
  onDelete,
  onClearActive,
  onTogglePinned,
}) => {
  const { t } = useTranslation(['common', 'tablePresets']);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(preset.name);

  const startEdit = useCallback(() => {
    setDraftName(preset.name);
    setIsEditing(true);
  }, [preset.name]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setDraftName(preset.name);
  }, [preset.name]);

  const commitEdit = useCallback(() => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== preset.name) {
      onRename(trimmed);
    }
    setIsEditing(false);
  }, [draftName, preset.name, onRename]);

  const pinLabel = preset.pinned ? t('tablePresets:unpinFromMenu') : t('tablePresets:pinToMenu');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit],
  );

  if (isEditing) {
    return (
      <Item variant="outline" size="sm" className={'p-2'}>
        <ItemHeader>
          <Input
            autoFocus
            size="sm"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <ItemActions className="ml-2 shrink-0">
            <Tooltip tooltip={t('common:save')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common:save')}
                icon={<CheckIcon className="size-3.5" />}
                onClick={commitEdit}
              />
            </Tooltip>
            <Tooltip tooltip={t('common:cancel')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common:cancel')}
                icon={<XIcon className="size-3.5" />}
                onClick={cancelEdit}
              />
            </Tooltip>
          </ItemActions>
        </ItemHeader>
      </Item>
    );
  }

  return (
    <Item
      variant="outline"
      size="sm"
      role="button"
      tabIndex={-1}
      onClick={onApply}
      className={cn(
        'group cursor-pointer hover:bg-muted/60 p-2 w-full min-w-0',
        isActive && 'border-primary bg-primary/5',
      )}
    >
      <ItemHeader className="min-w-0 min-h-9">
        <ItemTitle className="block w-full min-w-0 text-sm truncate">{preset.name}</ItemTitle>
        <ItemActions className="ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          {/* Показ по наведению задан обёрткой, а не классом на самой кнопке: базовый
              `ibs:inline-flex` из kit классом `hidden` не перебивается. */}
          <div className={cn(preset.pinned ? 'flex' : 'hidden group-hover:flex group-focus-within:flex')}>
            <Tooltip tooltip={pinLabel}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={pinLabel}
                aria-pressed={preset.pinned}
                icon={<StarIcon className={cn('size-3.5', preset.pinned && 'fill-current')} />}
                onClick={() => onTogglePinned(!preset.pinned)}
              />
            </Tooltip>
          </div>
          <div className="hidden group-hover:flex group-focus-within:flex items-center gap-2">
            {isActive && (
              <Tooltip tooltip={t('tablePresets:clearActive')}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('tablePresets:clearActive')}
                  icon={<BookmarkXIcon className="size-3.5" />}
                  onClick={onClearActive}
                />
              </Tooltip>
            )}
            <Tooltip tooltip={t('tablePresets:rename')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('tablePresets:rename')}
                icon={<PencilIcon className="size-3.5" />}
                onClick={startEdit}
              />
            </Tooltip>
            <ConfirmationButton
              title={t('tablePresets:deleteConfirmTitle', { name: preset.name })}
              description={t('tablePresets:deleteConfirmDescription')}
              onConfirm={onDelete}
              trigger={{ size: 'icon', 'aria-label': t('tablePresets:delete') }}
            />
          </div>
        </ItemActions>
      </ItemHeader>
    </Item>
  );
};
